import * as React from "react";
import { XIcon } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { Snackbar } from "../../../components/ui/snackbar";
import SaveView from "./SaveView";
import CheckInView from "./CheckInView";
import SaveToJyloView from "./SaveToJyloView";
import { CheckOutAction } from "../types/check-out-action";
import { DocumentCheckOutAdapter } from "../../../platform";

// Two shapes, because the two actions are not the same kind of thing.
//
// Save takes no input and runs the moment it is pressed, so it gets an ambient strip
// at the foot of the pane — the chat stays live and usable behind it. Check in is a
// decision with a comment attached, so it gets a bottom sheet that owns focus until
// the user commits or backs out. Saving a document that is not in Jylo yet asks where it
// goes, so it is a sheet too. None replaces the pane.

export interface CheckOutOverlayProps {
  documents: DocumentCheckOutAdapter;
  action: CheckOutAction | null;
  /** Changes on every ribbon press; remounts the surface so the action re-runs. */
  requestId: number;
  onClose: () => void;
  /** Save to Jylo stamped the document; the header must verify it again. */
  onLinked: () => void;
}

const CheckOutOverlay = ({
  documents,
  action,
  requestId,
  onClose,
  onLinked,
}: CheckOutOverlayProps) => {
  // Save to Jylo must not be dismissed mid-upload: the steps would carry on and their
  // outcome would be shown to no one.
  const busyRef = React.useRef(false);
  const reportBusy = React.useCallback((busy: boolean) => {
    busyRef.current = busy;
  }, []);

  return (
    <>
      <Snackbar open={action === "save"}>
        <div className="w-full rounded-[24px] border border-border bg-card shadow-md">
          <div className="flex flex-row items-start">
            <SaveView key={requestId} documents={documents} onClose={onClose} />
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Dismiss"
              title="Dismiss"
              className="m-2 text-muted-foreground"
            >
              <XIcon />
            </Button>
          </div>
        </div>
      </Snackbar>

      <Sheet
        open={action === "checkin"}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent accessibleTitle="Check in">
          {/* The grab handle every bottom sheet has: it says "this slides away" without a
            word, and gives the sheet a top edge that is not a hard line. */}
          <div className="mt-2 mb-3 h-1 w-9 self-center rounded-full bg-grey-200" />
          <CheckInView key={requestId} documents={documents} onClose={onClose} />
        </SheetContent>
      </Sheet>

      <Sheet
        open={action === "saveNew"}
        onOpenChange={(open) => {
          if (!open && !busyRef.current) onClose();
        }}
      >
        <SheetContent accessibleTitle="Save to Jylo" className="max-h-[88vh]">
          <div className="mt-2 mb-3 h-1 w-9 flex-none self-center rounded-full bg-grey-200" />
          <div className="min-h-0 overflow-y-auto">
            <SaveToJyloView
              key={requestId}
              documents={documents}
              onClose={onClose}
              onLinked={onLinked}
              onBusyChange={reportBusy}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CheckOutOverlay;
